import type { GitlabAiComponents } from "@p0security/integrations-gitlab-ai-shared/types";
import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreAdminToken,
  awsTf,
  gcpStoreAdminToken,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  GitlabAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<GitlabAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  instructions(
    context: InstallContext,
    group: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the admin token, if this secret manager has one */
  storeAdminToken(
    group: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: CredentialIac = {
  instructions: (context, group, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s GitLab connector, then click "Next".',
          command: gcpTf(context, group, secretManager),
        },
      ],
    },
  }),
  storeAdminToken: gcpStoreAdminToken,
} satisfies CredentialIac<"gcp-sm">;

const awsSmIac: CredentialIac = {
  instructions: (context, group, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s GitLab connector, then click "Next".',
          command: awsTf(context, group, secretManager),
        },
      ],
    },
  }),
  storeAdminToken: awsStoreAdminToken,
} satisfies CredentialIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  instructions: () => ({ commands: {} }),
  storeAdminToken: () => undefined,
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const AdminServiceAccountSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeAdminToken = credentialIac[secretManager.type].storeAdminToken(
    id,
    secretManager
  );
  return (
    <div>
      <Paragraph type="secondary">
        In GitLab, open the top-level group&apos;s Settings &gt; Service
        accounts, and add a service account for P0. You must be an Owner of the
        group, and GitLab may ask you to verify your identity first.
      </Paragraph>
      <Paragraph type="secondary">
        Add the service account to the group as an Owner, and create a personal
        access token for it with the <code>api</code> scope. P0 uses this token
        only to create and delete one service account for each grant.
      </Paragraph>
      {storeAdminToken && (
        <>
          <Paragraph>Admin token</Paragraph>
          <Paragraph type="secondary">
            Set <code>GITLAB_TOKEN</code> to the token, and run this command to
            update the secret to it. The connector cannot grant access until the
            secret holds the token.
          </Paragraph>
          <CommandDisplay commands={storeAdminToken} />
        </>
      )}
      <Paragraph>Group ID</Paragraph>
    </div>
  );
};

export const gitlabAiInstaller: FrontendInstaller<GitlabAiComponents> = {
  components: {
    credential: {
      instructions: (context, id, item) =>
        credentialIac[item.secretManager.type].instructions(
          context,
          id,
          item.secretManager
        ),
      items: {
        groupId: {
          labeler: (_, id, { secretManager, state }) =>
            state === "configure" && (
              <AdminServiceAccountSetup id={id} secretManager={secretManager} />
            ),
        },
        secretManager: {
          options: {
            "aws-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
